import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';
import ExtraSubjectEditModal from './ExtraSubjectEditModal';

/**
 * Локальный справочник предметов доп. занятий. Простая таблица без фильтров —
 * этот раздел используется редко (один раз завели, дальше только правят).
 * Из основной информации видим: название, привязку к 1С, статус привязки.
 */
const URL_SCHEMA = { archive: '0', edit: '' };

export default function ExtraSubjectsPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canWrite  = can('extra.subjects', 'write');
  const canDelete = can('extra.subjects', 'delete');

  const [params, setParams] = useUrlState(URL_SCHEMA);
  const [rows, setRows]     = useState([]);
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState(null);

  const reload = () => {
    setLoad(true); setError(null);
    api.get('/api/extra-subjects')
      .then((res) => setRows(res.rows || []))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoad(false));
  };
  useEffect(reload, []);

  const filtered = (params.archive === '1') ? rows : rows.filter((r) => !r.archive);

  const onDelete = async (s) => {
    if (!canDelete) return;
    if (!confirm(`Удалить предмет «${s.name}»? Если по нему есть занятия — лучше архивировать.`)) return;
    try {
      await api.delete('/api/extra-subjects/' + s.id);
      setRows((prev) => prev.filter((r) => r.id !== s.id));
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось удалить');
    }
  };

  const editing = (() => {
    if (!params.edit) return null;
    if (params.edit === 'new') return 'new';
    return rows.find((r) => r.id === parseInt(params.edit, 10)) || null;
  })();

  return (
    <>
      <Topbar
        breadcrumb={['Доп. занятия', 'Предметы']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />
      <div className="ref-page">
        <div className="ref-toolbar">
          <span className="ref-toolbar__title">
            Предметы {!loading && <span className="muted">· {filtered.length}</span>}
          </span>
          <select
            value={params.archive || '0'}
            onChange={(e) => setParams({ archive: e.target.value })}
            style={{
              marginLeft: 14, height: 30, padding: '0 8px',
              border: '1px solid var(--border-strong)', borderRadius: 6,
              fontSize: 13, background: 'var(--panel)', color: 'var(--fg)',
            }}
          >
            <option value="0">Активные</option>
            <option value="1">Все, включая архив</option>
          </select>
          <div className="ref-toolbar__spacer" />
          <button
            className="btn btn--primary btn--sm"
            onClick={() => setParams({ edit: 'new' })}
            disabled={!canWrite}
            title={canWrite ? undefined : 'Нет прав на запись'}
          >
            <Icon name="plus" size={13} /> Добавить предмет
          </button>
        </div>

        <div className="ref-content">
          {error && <div style={{ color: 'var(--red-fg)', padding: 16 }}>Ошибка: {error}</div>}
          {loading ? (
            <div className="ref-empty">Загрузка…</div>
          ) : filtered.length === 0 ? (
            <div className="ref-empty">Предметов нет. Добавьте первый.</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Название</th>
                    <th style={{ width: 220 }}>1С</th>
                    <th style={{ width: 100 }}>Статус</th>
                    <th style={{ width: 80 }} />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => (
                    <tr
                      key={s.id}
                      onClick={() => canWrite && setParams({ edit: String(s.id) })}
                      style={{ cursor: canWrite ? 'pointer' : 'default' }}
                    >
                      <td>
                        <div style={{ fontWeight: 500 }}>{s.name}</div>
                        {s.description && (
                          <div className="muted" style={{ fontSize: 12 }}>{s.description}</div>
                        )}
                      </td>
                      <td>
                        {s.onec
                          ? <span className="sd-onec is-on" title={s.onec.name || ''}>
                              {s.onec.code || '— нет кода —'}
                            </span>
                          : <span className="sd-onec is-off">не привязан</span>}
                      </td>
                      <td>
                        {s.archive
                          ? <span className="pay-st pay-st--mute">в архиве</span>
                          : <span className="pay-st pay-st--ok">активен</span>}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="ref-row-actions">
                          {canWrite && (
                            <button onClick={() => setParams({ edit: String(s.id) })} title="Редактировать">
                              <Icon name="edit" size={14} />
                            </button>
                          )}
                          {canDelete && (
                            <button className="is-danger" onClick={() => onDelete(s)} title="Удалить">
                              <Icon name="x" size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <ExtraSubjectEditModal
          mode={editing === 'new' ? 'create' : 'edit'}
          subject={editing === 'new' ? null : editing}
          onClose={() => setParams({ edit: '' })}
          onSaved={(s) => {
            setRows((prev) => {
              const i = prev.findIndex((r) => r.id === s.id);
              if (i >= 0) { const n = prev.slice(); n[i] = s; return n; }
              return [...prev, s];
            });
          }}
        />
      )}
    </>
  );
}
